import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  GraduationCap, ShieldCheck, TrendingUp, Award, Users, Sparkles,
  CheckCircle2, ArrowUpRight, Building2, Star, Briefcase, Globe2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_ethictrust-build/artifacts/0utie90a_ChatGPT%20Image%20Jun%2019%2C%202026%2C%2006_20_09%20PM.png";

const WHY_BULLETS = [
  "Improve Student Employability",
  "Strengthen Placement Outcomes",
  "Showcase Student Achievements",
  "Build Employer Confidence",
  "Create Lifelong Alumni Connections",
  "Enhance Institutional Reputation",
];

const PASSPORT_VALUE_PROPS = [
  { icon: ShieldCheck, label: "Professional Passport", desc: "One verifiable identity that travels with every graduate for life." },
  { icon: Award, label: "Verified Credentials", desc: "Every internship, project, certificate — issued, signed, and tamper-proof." },
  { icon: TrendingUp, label: "Placement Readiness", desc: "Recruiters trust EthicCred-issued institution records on first contact." },
  { icon: Sparkles, label: "Career Confidence", desc: "Students graduate with a portfolio, not just a piece of paper." },
];

export default function Institutions() {
  return (
    <div className="bg-ec-bg text-ec-ink min-h-screen" data-testid="institutions-page">
      <Navbar />
      <Hero />
      <ValueLines />
      <WhySection />
      <PassportShowcase />
      <RecognitionTease />
      <FinalCta />
      <Footer />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-12 lg:pt-28 lg:pb-16 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div
          className="lg:col-span-7"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 border border-ec-line bg-white px-3 py-1 text-xs eyebrow" data-testid="institutions-eyebrow">
            <GraduationCap className="w-3.5 h-3.5 text-ec-emerald" />
            For Institutions
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mt-6">
            Today's employers look <br />
            <span className="relative inline-block">
              beyond degrees.
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" preserveAspectRatio="none">
                <path d="M2 8 Q 75 2 150 6 T 298 4" fill="none" stroke="#059669" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-ec-ink-soft mt-7 max-w-2xl leading-relaxed">
            They look for <span className="font-semibold text-ec-ink">experience, achievements, professionalism, and credibility</span>.
            EthicCred helps institutions transform student achievements into verified professional identities.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3" data-testid="institutions-cta-row">
            <Link to="/register?role=institution" data-testid="cta-register-institute">
              <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 px-6 h-12 text-base gap-2">
                Register your Institute <ArrowUpRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/demo?audience=institution" data-testid="cta-request-demo">
              <Button variant="outline" className="rounded-sm border-ec-ink/20 px-6 h-12 text-base gap-2">
                Request a Demo
              </Button>
            </Link>
            <Link to="/login" data-testid="cta-signin">
              <Button variant="ghost" className="rounded-sm px-4 h-12 text-base text-ec-ink-soft hover:text-ec-ink">
                Sign in <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          </div>
          <p className="text-xs text-ec-ink-soft mt-4 font-mono-plex" data-testid="institutions-recognition-link">
            Already onboarded?{" "}
            <Link to="/get-recognized" className="underline text-ec-ink hover:text-ec-emerald">
              Apply for institution recognition →
            </Link>
          </p>
        </motion.div>

        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="relative bg-white border border-ec-ink/10 p-3 noise-overlay shadow-[0_30px_80px_-20px_rgba(0,0,0,0.18)]" data-testid="institutions-hero-image-wrap">
            <img src={HERO_IMAGE} alt="EthicCred institutions illustration showing students transitioning to professional careers in hospitality, aviation, hospitality, and beyond." className="w-full h-auto" data-testid="institutions-hero-image" />
            <div className="absolute -top-3 -left-3 px-3 py-1.5 bg-ec-emerald text-ec-ink font-display font-black text-xs rotate-[-3deg] shadow-md">
              CAREERS · NOT JUST DEGREES
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function ValueLines() {
  const lines = [
    "Every internship.",
    "Every project.",
    "Every achievement.",
    "Every recommendation.",
  ];
  return (
    <section className="border-y border-ec-line bg-ec-soft" data-testid="institutions-value-lines">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-16 text-center">
        <div className="space-y-2">
          {lines.map((l) => (
            <h2 key={l} className="font-display text-3xl sm:text-5xl font-black tracking-tight">{l}</h2>
          ))}
        </div>
        <div className="mt-8 inline-flex items-center gap-3 border border-ec-ink/15 px-5 py-3 bg-white">
          <ShieldCheck className="w-5 h-5 text-ec-emerald" />
          <p className="font-display text-base sm:text-xl font-bold tracking-tight">
            Connected through <span className="text-ec-emerald">one Professional Passport.</span>
          </p>
        </div>
      </div>
    </section>
  );
}

function WhySection() {
  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-10 py-20" data-testid="institutions-why">
      <div className="text-center mb-12">
        <div className="eyebrow">Why EthicCred</div>
        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
          Six outcomes every <br className="hidden sm:block" />placement office cares about.
        </h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {WHY_BULLETS.map((b, i) => (
          <motion.div
            key={b}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
            className="bg-white border border-ec-line p-5 flex items-start gap-3 hover:border-ec-emerald/40 transition-colors"
            data-testid={`why-bullet-${i + 1}`}
          >
            <CheckCircle2 className="w-5 h-5 text-ec-emerald mt-0.5 shrink-0" />
            <div className="font-semibold text-ec-ink">{b}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function PassportShowcase() {
  return (
    <section className="bg-ec-soft border-y border-ec-line" data-testid="institutions-passport-showcase">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
        <div className="text-center mb-12">
          <div className="eyebrow text-ec-emerald">Every graduate. Every time.</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
            Every student graduates with more <br className="hidden md:block" />
            than a certificate.
          </h2>
          <p className="text-ec-ink-soft mt-3 max-w-2xl mx-auto text-lg">
            They graduate with a <span className="font-semibold text-ec-ink">Professional Passport</span> — a verified, lifelong identity.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PASSPORT_VALUE_PROPS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-white border border-ec-ink/10 p-6 group hover:border-ec-emerald/40 transition-colors"
                data-testid={`passport-value-${i + 1}`}
              >
                <div className="w-11 h-11 bg-ec-ink text-white flex items-center justify-center group-hover:bg-ec-emerald group-hover:text-ec-ink transition-colors">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl font-bold mt-4">{p.label}</h3>
                <p className="text-sm text-ec-ink-soft mt-2 leading-relaxed">{p.desc}</p>
              </motion.div>
            );
          })}
        </div>
        <div className="mt-12 text-center">
          <div className="inline-block border-y-2 border-ec-ink py-3 px-8">
            <div className="eyebrow text-ec-ink-soft">ETHICCRED</div>
            <h3 className="font-display text-2xl sm:text-3xl font-black mt-1">Your Professional Passport for Life</h3>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecognitionTease() {
  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-10 py-20" data-testid="institutions-recognition-tease">
      <div className="bg-ec-ink text-white p-8 sm:p-12 relative overflow-hidden noise-overlay">
        <div className="absolute -top-10 -right-10 opacity-10">
          <Award className="w-64 h-64" />
        </div>
        <div className="relative grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 px-3 py-1 text-xs eyebrow text-white">
              <Star className="w-3 h-3 text-ec-emerald" />
              Recognition Program
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-5">
              Get Recognized. <br />
              Stand Out. Build Trust.
            </h2>
            <p className="text-zinc-300 mt-4 leading-relaxed">
              From <span className="text-ec-emerald font-semibold">Verified Institution</span> to{" "}
              <span className="text-ec-emerald font-semibold">Top Placement Institution</span>, EthicCred offers
              five tiers of recognition that signal credibility to students, parents, and employers.
            </p>
            <Link to="/get-recognized" data-testid="cta-get-recognized">
              <Button className="rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink px-6 h-12 mt-6 text-base font-bold gap-2">
                Explore Recognition <ArrowUpRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          <ul className="space-y-2 text-sm">
            {[
              "Verified Institution",
              "Certified Institution",
              "Top Institution",
              "Top Placement Institution",
              "Student Excellence Institution",
            ].map((t, i) => (
              <li key={t} className="flex items-center gap-3 border-b border-white/10 pb-2" data-testid={`recognition-tease-${i + 1}`}>
                <Award className="w-4 h-4 text-ec-emerald" />
                <span className="font-display font-semibold">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="max-w-5xl mx-auto px-6 sm:px-10 py-20 text-center" data-testid="institutions-final-cta">
      <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight">
        Ready to graduate <br />
        <span className="text-ec-emerald">verified professionals?</span>
      </h2>
      <p className="text-ec-ink-soft mt-4 max-w-xl mx-auto text-lg">
        Onboard your institution in under 10 minutes. Pilot the program with a single batch. Scale across all campuses when you're ready.
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link to="/register?role=institution"><Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 px-6 h-12 text-base gap-2" data-testid="final-cta-register">Register your Institute <ArrowUpRight className="w-4 h-4" /></Button></Link>
        <Link to="/demo?audience=institution"><Button variant="outline" className="rounded-sm border-ec-ink/20 px-6 h-12 text-base" data-testid="final-cta-demo">Request a Demo</Button></Link>
        <Link to="/login"><Button variant="ghost" className="rounded-sm px-4 h-12 text-base text-ec-ink-soft" data-testid="final-cta-signin">Sign in</Button></Link>
      </div>
    </section>
  );
}
